import React, { useEffect, useState } from "react";
import { Input, Modal, Select } from "antd";

import {
  DEFAULT_BOARD_COLUMN_COLORS,
} from "../../constants/projects.constants";
import { errMsg } from "../../helpers/errMsg.helper";

import type { Label } from "../../../../core/data/redux/projects/types";

interface LabelEditorProps {
  open: boolean;
  initialLabel?: Label | null;
  onSubmit: (payload: { name: string; color: string }) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

const LabelEditor: React.FC<LabelEditorProps> = ({
  open,
  initialLabel,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const isEdit = Boolean(initialLabel);
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_BOARD_COLUMN_COLORS[1]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initialLabel) {
      setName(initialLabel.name);
      setColor(initialLabel.color || DEFAULT_BOARD_COLUMN_COLORS[1]);
    } else {
      setName("");
      setColor(DEFAULT_BOARD_COLUMN_COLORS[1]);
    }
    setError(null);
  }, [open, initialLabel]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Label name is required");
      return;
    }
    try {
      setError(null);
      await onSubmit({ name: name.trim(), color });
    } catch (e: any) {
      setError(errMsg(e, "Failed to save label"));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isEdit ? "Edit Label" : "New Label"}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden" },
        header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
        body: { padding: "16px 24px 20px" },
        footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-light"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-info"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
          >
            {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Label"}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {error && (
          <div className="alert alert-danger py-2 mb-0">{error}</div>
        )}
        <div>
          <label className="form-label small fw-semibold">
            Name <span className="text-danger">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. needs-review"
            maxLength={64}
          />
        </div>
        <div>
          <label className="form-label small fw-semibold">Color</label>
          <Select
            style={{ width: "100%" }}
            value={color}
            onChange={(v) => setColor(v)}
            options={DEFAULT_BOARD_COLUMN_COLORS.map((c) => ({
              value: c,
              label: (
                <span className="d-inline-flex align-items-center gap-2">
                  <span
                    className="d-inline-block rounded-circle"
                    style={{ width: 12, height: 12, background: c }}
                  />
                  {c}
                </span>
              ),
            }))}
          />
        </div>
      </div>
    </Modal>
  );
};

export default LabelEditor;
